import React from 'react';
import { useNavigate } from 'react-router';
import { usePolicies, useCreatePolicy } from '@redbax/shared';
import {
  PageHeader,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
  Label,
  EmptyState,
  LoadingSkeleton,
} from '@redbax/ui-kit';
import { Plus, Shield } from 'lucide-react';

function statusBadgeVariant(status: string) {
  switch (status) {
    case 'Active':
      return 'success' as const;
    case 'Draft':
      return 'warning' as const;
    case 'Archived':
      return 'secondary' as const;
    default:
      return 'outline' as const;
  }
}

export function PoliciesPage() {
  const navigate = useNavigate();
  const { data: policies, isLoading } = usePolicies();
  const createPolicy = useCreatePolicy();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');

  const handleCreate = () => {
    if (!name.trim()) return;
    createPolicy.mutate(
      { name, description: description || undefined },
      {
        onSuccess: () => {
          setDialogOpen(false);
          setName('');
          setDescription('');
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Policies" description="Manage detection policies and rules" />
        <LoadingSkeleton rows={4} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Policies"
        description="Manage detection policies and rules"
        actions={
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger className="inline-flex items-center gap-2 rounded-md bg-red-accent px-4 py-2 text-sm font-medium text-text-primary hover:bg-red-hover">
              <Plus className="h-4 w-4" />
              New Policy
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Policy</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="policy-name">Name</Label>
                  <Input
                    id="policy-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., PII Protection"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="policy-desc">Description</Label>
                  <Input
                    id="policy-desc"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Optional description"
                    className="mt-1"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleCreate} disabled={!name.trim()}>
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {!policies || policies.length === 0 ? (
        <EmptyState
          icon={<Shield className="h-12 w-12" />}
          title="No policies yet"
          description="Create your first detection policy to start protecting your data."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {policies.map((policy) => (
            <Card
              key={policy.id}
              className="cursor-pointer transition-colors hover:border-red-accent/50"
              onClick={() => navigate(`/policies/${policy.id}`)}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{policy.name}</CardTitle>
                  <Badge variant={statusBadgeVariant(policy.status)}>{policy.status}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-text-secondary">
                  {policy.description || 'No description'}
                </p>
                <p className="mt-2 text-xs text-text-secondary">
                  {policy.rules.length} rule{policy.rules.length !== 1 ? 's' : ''}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
